import { useParams } from 'next/navigation'
import { useState } from 'react'
import { useTexts } from '@/contexts/LanguageContext'
import { useSnackbar } from '@/contexts/SnackBarContext'
import { requestAccess } from '@/services/trust-center'
import { isServiceError } from '@/types/error'

export interface IRequestAccessFields {
  name: string
  email: string
  company: string
  reason: string
}

const EMPTY_FIELDS: IRequestAccessFields = { name: '', email: '', company: '', reason: '' }
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NAME_SEPARATOR = ' '

export const isValidEmail = (email: string) => EMAIL_REGEX.test(email)

// The API takes first and last name separately
const splitName = (name: string) => {
  const [firstName, ...rest] = name.trim().split(/\s+/)
  return { firstName, lastName: rest.join(NAME_SEPARATOR) }
}

export function useRequestAccessForm(defaultSelected: string[]) {
  const { orgToken } = useParams<{ orgToken: string }>()
  const { ERROR } = useTexts().PUBLIC_TRUST_CENTER
  const showSnackbar = useSnackbar()
  const [fields, setFields] = useState<IRequestAccessFields>(EMPTY_FIELDS)
  const [selected, setSelected] = useState<string[]>(defaultSelected)
  const [isSending, setIsSending] = useState(false)
  const [isSent, setIsSent] = useState(false)

  const isValid = Boolean(fields.name.trim()) && isValidEmail(fields.email) && selected.length > 0

  const setField = (field: keyof IRequestAccessFields, value: string) =>
    setFields((previous) => ({ ...previous, [field]: value }))

  const toggle = (option: string) =>
    setSelected((previous) =>
      previous.includes(option) ? previous.filter((item) => item !== option) : [...previous, option],
    )

  const reset = () => {
    setFields(EMPTY_FIELDS)
    setSelected(defaultSelected)
    setIsSent(false)
  }

  const submit = async () => {
    setIsSending(true)
    const response = await requestAccess({
      organizationToken: orgToken,
      requestAccessForm: {
        ...splitName(fields.name),
        email: fields.email,
        company: fields.company,
        reason: fields.reason,
      },
      resourceNames: selected,
    })
    setIsSending(false)
    if (isServiceError(response)) {
      showSnackbar(ERROR.SOMETHING_WENT_WRONG, 'error')
      return
    }
    setIsSent(true)
  }

  return {
    fields,
    selected,
    isValid,
    isSending,
    isSent,
    setField,
    toggle,
    reset,
    submit,
  }
}

export type IRequestAccessFormState = ReturnType<typeof useRequestAccessForm>
